import React from 'react';
import { useIDEStore } from '../../stores/ideStore';
import { 
  Box, Eye, Sun, Type, Move, RotateCw, Maximize, 
  Trash2, Plus, GripVertical, Settings 
} from 'lucide-react';

// --- Generic Input Components ---

const Vector3Input = ({ label, values, onChange }: any) => (
  <div className="flex items-center gap-2 mb-2">
    <div className="w-16 text-xs text-gray-400 flex items-center gap-1">
      {label === 'Position' && <Move size={10} />}
      {label === 'Rotation' && <RotateCw size={10} />}
      {label === 'Scale' && <Maximize size={10} />}
      {label}
    </div>
    <div className="flex-1 flex gap-1">
      {['x', 'y', 'z'].map((axis) => (
        <div key={axis} className="flex-1 flex items-center bg-[#2a2a2a] rounded border border-[#333] overflow-hidden group focus-within:border-[#375eab]">
          <div className={`
            w-4 h-full flex items-center justify-center text-[10px] font-bold cursor-ew-resize
            ${axis === 'x' ? 'text-red-400' : axis === 'y' ? 'text-green-400' : 'text-blue-400'}
            bg-[#222] group-hover:bg-[#333]
          `}>
            {axis.toUpperCase()}
          </div>
          <input
            type="number"
            value={values[axis] || 0}
            onChange={(e) => onChange(axis, parseFloat(e.target.value))}
            className="w-full bg-transparent text-xs text-white px-1 py-1 outline-none"
            step={0.1}
          />
        </div>
      ))}
    </div>
  </div>
);

const NumberInput = ({ label, value, onChange, step = 1 }: any) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-xs text-gray-400">{label}</span>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-24 bg-[#2a2a2a] border border-[#333] rounded text-xs text-white px-2 py-1 outline-none focus:border-[#375eab]"
      step={step}
    />
  </div>
);

const ColorInput = ({ label, value, onChange }: any) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-xs text-gray-400">{label}</span>
    <div className="flex gap-2 items-center">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-6 bg-transparent cursor-pointer border-0 p-0"
      />
      <span className="text-xs text-gray-500 font-mono">{value}</span>
    </div>
  </div>
);

const CheckboxInput = ({ label, value, onChange }: any) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-xs text-gray-400">{label}</span>
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-gray-600 bg-[#2a2a2a] text-[#375eab] focus:ring-0 focus:ring-offset-0"
    />
  </div>
);

// --- Component Container ---

const ComponentHeader = ({ icon: Icon, title, onRemove }: any) => (
  <div className="flex items-center bg-[#333] px-2 py-1.5 border-b border-[#222]">
    <Icon size={14} className="text-gray-400 mr-2" />
    <span className="text-xs font-bold text-gray-200 flex-1">{title}</span>
    <button className="text-gray-500 hover:text-white">
      <Settings size={12} />
    </button>
    {onRemove && (
      <button onClick={onRemove} className="text-gray-500 hover:text-red-400 ml-2">
        <Trash2 size={12} />
      </button>
    )}
  </div>
);

// --- Specific Components ---

const TransformComponent = ({ data, onChange }: any) => (
  <div className="mb-px">
    <ComponentHeader icon={Move} title="Transform" />
    <div className="bg-[#252526] p-3">
      <Vector3Input
        label="Position"
        values={data.position || { x: 0, y: 0, z: 0 }}
        onChange={(axis: string, val: number) => 
          onChange({ position: { ...(data.position || {}), [axis]: val } })
        }
      />
      <Vector3Input
        label="Rotation"
        values={data.rotation || { x: 0, y: 0, z: 0 }}
        onChange={(axis: string, val: number) => 
          onChange({ rotation: { ...(data.rotation || {}), [axis]: val } })
        }
      />
      <Vector3Input
        label="Scale"
        values={data.scale || { x: 1, y: 1, z: 1 }}
        onChange={(axis: string, val: number) => 
          onChange({ scale: { ...(data.scale || {}), [axis]: val } })
        }
      />
    </div>
  </div>
);

const LightComponent = ({ data, onChange, onRemove }: any) => (
  <div className="mb-px">
    <ComponentHeader icon={Sun} title="Light" onRemove={onRemove} />
    <div className="bg-[#252526] p-3">
      <ColorInput
        label="Color"
        value={data.color || '#ffffff'}
        onChange={(val: string) => onChange({ color: val })}
      />
      <NumberInput
        label="Intensity"
        value={data.intensity || 1}
        onChange={(val: number) => onChange({ intensity: val })}
        step={0.1}
      />
      <CheckboxInput
        label="Cast Shadows"
        value={data.castShadows || false}
        onChange={(val: boolean) => onChange({ castShadows: val })}
      />
    </div>
  </div>
);

const CameraComponent = ({ data, onChange, onRemove }: any) => (
  <div className="mb-px">
    <ComponentHeader icon={Eye} title="Camera" onRemove={onRemove} />
    <div className="bg-[#252526] p-3">
      <NumberInput
        label="Field of View"
        value={data.fov || 60}
        onChange={(val: number) => onChange({ fov: val })}
      />
      <NumberInput
        label="Near Clip"
        value={data.near || 0.3}
        onChange={(val: number) => onChange({ near: val })}
        step={0.1}
      />
      <NumberInput
        label="Far Clip"
        value={data.far || 1000}
        onChange={(val: number) => onChange({ far: val })}
        step={10}
      />
    </div>
  </div>
);

// --- Main Panel ---

export function InspectorPanel({ className }: { className?: string }) {
  const { gameObjects, selectedObjectId, updateGameObject } = useIDEStore();
  
  const selectedObject = gameObjects.find(obj => obj.id === selectedObjectId);

  if (!selectedObject) {
    return (
      <div className={`inspector-panel ${className || ''} flex items-center justify-center bg-[#1e1e1e] text-gray-500`}>
        <p className="text-xs">No object selected</p>
      </div>
    );
  }

  const handleUpdate = (updates: any) => {
    updateGameObject(selectedObject.id, updates);
  };

  const handleUpdateComponent = (index: number, updates: any) => {
    const newComponents = [...(selectedObject.components || [])];
    newComponents[index] = { ...newComponents[index], properties: { ...newComponents[index].properties, ...updates } };
    handleUpdate({ components: newComponents });
  };

  const addComponent = (type: string) => {
    const newComponents = [...(selectedObject.components || [])];
    newComponents.push({
      type,
      properties: type === 'Light' ? { color: '#ffffff', intensity: 1 } : { fov: 60 }
    });
    handleUpdate({ components: newComponents });
  };

  return (
    <div className={`inspector-panel ${className || ''} flex flex-col h-full bg-[#1e1e1e] overflow-y-auto`}>
      {/* Header */}
      <div className="p-3 bg-[#252526] border-b border-[#333]">
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={selectedObject.visible !== false} // Default true
            onChange={(e) => handleUpdate({ visible: e.target.checked })}
            className="w-4 h-4"
          />
          <input
            type="text"
            value={selectedObject.name}
            onChange={(e) => handleUpdate({ name: e.target.value })}
            className="flex-1 bg-[#1e1e1e] border border-[#333] rounded px-2 py-1 text-sm text-white font-bold outline-none focus:border-[#375eab]"
          />
          <input
            type="checkbox"
            checked={selectedObject.static || false}
            onChange={(e) => handleUpdate({ static: e.target.checked })}
            className="w-4 h-4"
            title="Static"
          />
        </div>
        <div className="flex gap-2">
          <select className="flex-1 bg-[#1e1e1e] border border-[#333] rounded px-2 py-1 text-[10px] text-gray-400 outline-none">
            <option>Untagged</option>
            <option>Player</option>
            <option>Enemy</option>
          </select>
          <select className="flex-1 bg-[#1e1e1e] border border-[#333] rounded px-2 py-1 text-[10px] text-gray-400 outline-none">
            <option>Default Layer</option>
            <option>UI</option>
            <option>Water</option>
          </select>
        </div>
      </div>

      {/* Components Stack */}
      <div className="flex-1">
        {/* Transform is always first */}
        <TransformComponent 
          data={selectedObject} // Pass root object for transform props
          onChange={handleUpdate}
        />

        {/* Dynamic Components */}
        {selectedObject.components?.map((comp: any, idx: number) => {
          if (comp.type === 'Light') {
            return (
              <LightComponent 
                key={idx} 
                data={comp.properties} 
                onChange={(updates: any) => handleUpdateComponent(idx, updates)}
                onRemove={() => {
                  const newComps = [...selectedObject.components];
                  newComps.splice(idx, 1);
                  handleUpdate({ components: newComps });
                }}
              />
            );
          }
          if (comp.type === 'Camera') {
            return (
              <CameraComponent 
                key={idx} 
                data={comp.properties} 
                onChange={(updates: any) => handleUpdateComponent(idx, updates)}
                onRemove={() => {
                  const newComps = [...selectedObject.components];
                  newComps.splice(idx, 1);
                  handleUpdate({ components: newComps });
                }}
              />
            );
          }
          return null;
        })}

        {/* Add Component Button */}
        <div className="p-4 flex justify-center">
          <button 
            className="flex items-center gap-2 px-6 py-2 bg-[#2a2a2a] hover:bg-[#333] border border-[#444] rounded-full text-xs text-white transition-colors"
            onClick={() => addComponent('Light')} // Simple mock action
          >
            <Plus size={14} /> Add Component
          </button>
        </div>
      </div>
    </div>
  );
}